/**
 * Photosensitivity flash limiter (SAF-1). Fed the *measured* mean luminance of each rendered
 * frame (before gain), it returns a gain that limits how fast the displayed brightness may rise.
 *
 * WCAG 2.3.1 ("three flashes"): a flash is a pair of opposing luminance changes of ≥ 10%. Rises
 * are rate-limited; if flashes are still counted in the last second, the limit tightens. Falls
 * are never limited (darkening is safe). Pure, so it is unit-tested with synthetic sequences.
 */

const BASE_RISE_PER_S = 2.0;
const STRICT_RISE_PER_S = 0.35;
const FLASH_DELTA = 0.1;

export class FlashLimiter {
  private displayed = 0;
  private readonly flashTimes: number[] = [];
  private lastExtreme = 0;
  private direction: 1 | -1 | 0 = 0;
  private time = 0;

  /**
   * @param measured mean luminance 0..1 of the frame about to be shown, before gain
   * @param dt seconds since the previous frame
   * @returns gain 0..1 to apply
   */
  update(measured: number, dt: number, enabled: boolean): number {
    this.time += dt;
    let gain = 1;
    if (enabled) {
      const maxRise = (this.flashesInLastSecond() >= 2 ? STRICT_RISE_PER_S : BASE_RISE_PER_S) * dt;
      const allowed = this.displayed + maxRise;
      if (measured > allowed && measured > 1e-4) gain = Math.max(0.05, allowed / measured);
    }
    const shown = measured * gain;
    this.trackFlashes(shown);
    this.displayed = shown;
    return gain;
  }

  /** Displayed luminance after limiting (for tests and the HUD). */
  get luminance(): number {
    return this.displayed;
  }

  flashesInLastSecond(): number {
    while (this.flashTimes.length && this.flashTimes[0]! < this.time - 1) this.flashTimes.shift();
    return this.flashTimes.length;
  }

  private trackFlashes(lum: number): void {
    const delta = lum - this.lastExtreme;
    if (this.direction >= 0 && delta <= -FLASH_DELTA) {
      if (this.direction === 1) this.flashTimes.push(this.time); // up then down = one flash
      this.direction = -1;
      this.lastExtreme = lum;
    } else if (this.direction <= 0 && delta >= FLASH_DELTA) {
      this.direction = 1;
      this.lastExtreme = lum;
    } else if (
      (this.direction === 1 && lum > this.lastExtreme) ||
      (this.direction === -1 && lum < this.lastExtreme)
    ) {
      this.lastExtreme = lum;
    } else if (this.direction === 0) {
      this.lastExtreme = lum;
    }
  }
}
